export type IntentId = 'genetics' | 'health-records' | 'goals' | 'explore'

/** Coach first-run prompts — aligned with mobile firstRunIntentOptions. */
export const FIRST_RUN_INTAKE_PROMPTS: Record<
  IntentId,
  { displayText: string; prompt: string }
> = {
  genetics: {
    displayText: 'I want to understand my genetics',
    prompt:
      'I want to understand my genetics and how they relate to my health and daily wellness.',
  },
  'health-records': {
    displayText: "I'm managing a health condition",
    prompt:
      "I'm managing a health condition and want help understanding what to focus on for my day-to-day wellness.",
  },
  goals: {
    displayText: "I'm building a healthy habit",
    prompt:
      "I'm building a healthy habit and want a coach that can help me stay on track with practical next steps.",
  },
  explore: {
    displayText: "I'm just exploring Livewello",
    prompt:
      "I'm just exploring Livewello and want to see how the Wellness Coach can help me with my health.",
  },
}

export interface Intent {
  id: IntentId
  label: string
  description: string
  icon: string
  route: string
  routeParam?: string
  bullets: string[]
  loomUrl?: string
}

export const INTENTS: Intent[] = [
  {
    id: 'genetics',
    label: 'Make sense of my DNA',
    description: 'Turn raw genetic data into answers you can act on',
    icon: 'dna',
    route: '/chat',
    bullets: [
      'Ask questions about your genetic variants in plain language',
      'Order a Livewello DNA kit, or upload raw data from 23andMe, AncestryDNA, or any lab',
      'Get personalized insights based on your actual genotype',
    ],
  },
  {
    id: 'health-records',
    label: 'Get on top of a health condition',
    description: 'Keep labs and notes in one place, so nothing gets lost',
    icon: 'clipboard',
    route: '/chat',
    bullets: [
      'Add blood work, imaging reports, and clinical notes',
      'Organise records by category for quick access',
      'Share specific records with your care team',
    ],
  },
  {
    id: 'goals',
    label: 'Build a habit that sticks',
    description: 'Stay consistent with goals shaped by your own health data',
    icon: 'target',
    route: '/chat',
    bullets: [
      'Define a goal and track check-ins over time',
      'Connect goals to your genetic and health record data',
      'Invite a Goals Partner to keep you on track',
    ],
  },
  {
    id: 'explore',
    label: 'See what Livewello can do',
    description: 'Look around at your own pace — nothing to set up first',
    icon: 'compass',
    route: '/chat',
    bullets: [
      'Your dashboard shows everything at a glance',
      'Use the sidebar to navigate any section',
      'Come back here any time — open the account menu (top right) and tap "Show me around"',
      'Working on something bigger? "Work with a coach" in the same menu shows your options',
    ],
  },
]
